import axiosInstance from '@/services/utils/apiInstance'
import ResponseConstant from '@/constants/responseConstant'
import router from '@/router'
import { useAuthStore } from '@/store/authStore'
import {
  deobfuscatePayload,
  getObfuscationVersion,
  isObfuscationEnabled,
  isObfuscationEnvelope,
  obfuscatePayload
} from '@/services/utils/obfuscation'

const EXCLUDED_URLS = [
  '/auth/login',
  '/auth/login/finalize',
  '/auth/login/collaborator',
  '/auth/login/2fa/verify',
  '/auth/login/2fa/resend',
  '/auth/refresh-token',
  '/auth/logout',
  '/distributor/account-creation-request',
  '/forgot-password',
  '/reset-password',
  '/initialization-password'
]

function isExcludedUrl(url) {
  return EXCLUDED_URLS.includes(url?.split('?')[0])
}

function safelyEncodeUrlSegment(segment) {
  if (segment.length === 0) {
    return segment
  }

  try {
    return encodeURIComponent(decodeURIComponent(segment))
  } catch {
    return encodeURIComponent(segment)
  }
}

function normalizeUrlPath(url) {
  if (!url || /^https?:\/\//i.test(url)) {
    return url
  }

  const [pathname, queryString] = url.split('?')
  const encodedPathname = pathname
    .split('/')
    .map((segment) => safelyEncodeUrlSegment(segment))
    .join('/')

  if (!queryString) {
    return encodedPathname
  }

  return `${encodedPathname}?${queryString}`
}

function clearFormDataContentType(headers) {
  if (!headers) {
    return
  }

  if (typeof headers.delete === 'function') {
    headers.delete('Content-Type')
    headers.delete('content-type')
    return
  }

  delete headers['Content-Type']
  delete headers['content-type']
}

function getHeader(headers, name) {
  if (!headers) {
    return undefined
  }

  if (typeof headers.get === 'function') {
    return headers.get(name)
  }

  return headers[name] ?? headers[name.toLowerCase()]
}

function hasObfuscationHeader(headers) {
  return getHeader(headers, 'X-Obfuscation') === getObfuscationVersion()
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
  return match ? match[2] : null
}

const initializeInterceptor = () => {
  let refreshPromise = null

  function refreshSession() {
    if (!refreshPromise) {
      refreshPromise = axiosInstance
        .get('/auth/refresh-token')
        .catch(async (error) => {
          await useAuthStore().logoutAction()
          await router.push('/')
          throw error
        })
        .finally(() => {
          refreshPromise = null
        })
    }
    return refreshPromise
  }

  axiosInstance.interceptors.request.use(
    (config) => {
      config.url = normalizeUrlPath(config.url)

      const csrf = getCookie('client_csrf_token')
      if (csrf) {
        config.headers['X-CSRF-Token'] = csrf
      }

      if (isObfuscationEnabled()) {
        config.headers['X-Obfuscation-Accept'] = getObfuscationVersion()
      }

      if (
        isObfuscationEnabled() &&
        config.data !== undefined &&
        config.data !== null &&
        !hasObfuscationHeader(config.headers) &&
        !(config.data instanceof FormData)
      ) {
        config.headers['X-Obfuscation'] = getObfuscationVersion()
        config.data = obfuscatePayload(config.data)
      }

      if (config.data instanceof FormData) {
        clearFormDataContentType(config.headers)
      }

      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  axiosInstance.interceptors.response.use(
    (response) => {
      if (
        isObfuscationEnabled() &&
        response.headers?.['x-obfuscation'] === getObfuscationVersion() &&
        isObfuscationEnvelope(response.data)
      ) {
        response.data = deobfuscatePayload(response.data)
      }

      return response
    },
    async (error) => {
      const originalConfig = error.config
      const status = error.response?.status

      if (
        isObfuscationEnabled() &&
        error.response?.headers?.['x-obfuscation'] === getObfuscationVersion() &&
        isObfuscationEnvelope(error.response?.data)
      ) {
        error.response.data = deobfuscatePayload(error.response.data)
      }

      if (
        originalConfig?.publicCapability ||
        isExcludedUrl(originalConfig?.url) ||
        !error.response
      ) {
        return Promise.reject(error)
      }

      if (status === 401) {
        const authStore = useAuthStore()

        if (error.response.data === ResponseConstant.invalid_csrf_error.toLowerCase()) {
          await authStore.logoutAction()
          await router.push('/')
          return Promise.reject(error)
        }

        if (!originalConfig._retry) {
          originalConfig._retry = true
          await refreshSession()
          if (originalConfig.data instanceof FormData) {
            clearFormDataContentType(originalConfig.headers)
          }
          return axiosInstance(originalConfig)
        }
      }

      if (status === 403) {
        await router.push('/')
        return Promise.reject(error)
      }

      return Promise.reject(error)
    }
  )
}

export default initializeInterceptor
